import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'reactstrap';
import './LoiAndTermsForm.scss';
import { TermsFormDisplay } from './TermsFormDisplay';
import { VendorContractsTable } from './VendorContractsTable';
import { OwnerEmploymentAgreementDurationTable } from './OwnerEmploymentAgreementDurationTable';
import { LoiForm, SellerLocation } from '../../../../../../slices/marketplace/loi-form/loi-form';
import { SellingOwnerEmploymentAgreementRow, TermForm, TermFormLocation, VendorContractRow, createEmptyTermForm } from '../../../../../../slices/marketplace/loi-form/term-form';
import { useStateSelector } from '../../../../../../store/selectors';
import createNotification from '../../../../../../utils/createNotification';
import ButtonLoader from '../../../../../../components/Layout/Buttons/ButtonLoader';
import { TermsFormSelect } from './TermsFormSelect';
import { EmployeeConsiderationPayer, EmployeeConsiderationPayers } from '../../../../../../slices/marketplace/loi-form/employee-consideration-payer.enum';
import { LoiFormAnswerType, LoiFormAnswerTypeList } from '../../../../../../slices/marketplace/loi-form/loi-form-answer-type.enum';
import { SellerLegalInfoSection } from './SellerLegalInfoSection';
import { approvePendingChange, rejectPendingChange, setVendorContractAssumption } from '../../../../../../slices/marketplace/loi-form/loi-forms-api';
import { PendingChangeControl } from './PendingChangeControl';
import {
    PendingFieldChange,
    PendingFieldChangeKey,
    findPendingFieldChange,
    groupPendingFieldChangesByRowKey,
} from '../../../../../../slices/marketplace/loi-form/pending-field-change';

const YES_NO_OPTIONS = [{ value: 'Yes' }, { value: 'No' }] as const;

const DEFAULT_LEASE_TYPE = LoiFormAnswerTypeList.find(f => f.id === LoiFormAnswerType.LeaseType).options[0].value;

const LEASE_ASSUMPTION_OPTIONS = [
    { value: 'existing', label: 'Assume Existing Lease' },
    { value: 'new', label: 'Execute New Lease' },
] as const;

const CONSIDERATION_PAYER_OPTIONS = EmployeeConsiderationPayers.map(p => ({ value: String(p.id), label: p.name }));

function booleanToYesNo(value: boolean | null | undefined): string {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '';
}

function yesNoToBoolean(value: string | null): boolean | null {
    if (value === 'Yes') return true;
    if (value === 'No') return false;
    return null;
}

interface LoiAndTermsFormProps {
    formData: LoiForm;
    isReadonly: boolean;
    isInitialQuestionsReadonly?: boolean;
    deferAssumptionSave?: boolean;
    onChange: (termForm: TermForm) => void;
    errors?: Set<string>;
    opportunityId?: number;
    loiFormId?: string;
    uploadedDocumentName?: string;
    documentDetailsId?: string;
    onUploadComplete?: () => void;
    loiFileError?: boolean;
    isInitialSubmission?: boolean;
    isFsb?: boolean;
    isSfas?: boolean;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    onFieldCommentChange?: (fieldKey: string, comment: string | null) => void;
    onAdminCommentChange?: (fieldKey: string, adminComment: string | null) => void;
    sellerLocations: SellerLocation[];
    isAdmin?: boolean;
    isBuyer?: boolean;
    hasVendorContracts?: boolean;
    vendorContractColumns?: string[];
    vendorContractRowDisplayData?: Record<number, Record<string, string>>;
    vendorContractTableRows?: Array<{ tableRowId: number }>;
    vendorContractProjectId?: number | null;
    vendorContractTableId?: number | null;
    vendorContractFileColumnName?: string | null;
    vendorContractFileColumnId?: number | null;
    hasOwnerEmploymentAgreement?: boolean;
    ownerEmploymentColumns?: string[];
    ownerEmploymentRowDisplayData?: Record<string, Record<string, string>>;
    ownerEmploymentTableRows?: Array<{ tableRowId: number; customRowId: string }>;
    isQuestionsOnly?: boolean;
    isTermsOnly?: boolean;
    canEditFairValues?: boolean;
    canApprovePendingChanges?: boolean;
    onPendingChangeResolved?: (change: PendingFieldChange, approved: boolean) => void;
    onDiscardPendingChange?: (fieldKey: PendingFieldChangeKey, rowKey: string | null) => Promise<void>;
}

export const LoiAndTermsForm = (props: LoiAndTermsFormProps) => {
    const termForm: TermForm = props.formData.termForm ?? createEmptyTermForm();
    const axios = useStateSelector((state) => state.core.axios);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [localUploadedFileName, setLocalUploadedFileName] = useState<string | undefined>(undefined);

    const uploadedFileName = localUploadedFileName ?? props.uploadedDocumentName;

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !axios || !props.opportunityId || !props.loiFormId) {
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const response = await axios.post(
                `/api/Marketplace/${props.opportunityId}/MarketplaceOpportunityDocuments/UploadLoiProcessDocument/${props.loiFormId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            if (response.status !== 200) {
                const message = (response as any)?.response?.data?.detail;
                createNotification(
                    message ?? 'An error occurred while uploading the file',
                    'error'
                );
            } else {
                setLocalUploadedFileName(file.name);
                createNotification('File uploaded successfully', 'success');
                props.onUploadComplete?.();
            }
        } catch {
            createNotification('An error occurred while uploading the file', 'error');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const pendingFieldChanges = props.formData.pendingFieldChanges;
    const vendorPendingChangesByRow = groupPendingFieldChangesByRowKey(pendingFieldChanges, 'vendorAssumption');
    const ownerEmployedPostClosePendingChangesByRow = groupPendingFieldChangesByRowKey(pendingFieldChanges, 'ownerEmployedPostClose');
    const ownerEmploymentDurationPendingChangesByRow = groupPendingFieldChangesByRowKey(pendingFieldChanges, 'ownerEmploymentDuration');

    const handleApprovePendingChange = async (change: PendingFieldChange) => {
        if (!axios || !props.loiFormId) return;
        await approvePendingChange(axios, props.loiFormId, change.id);
        props.onPendingChangeResolved?.(change, true);
    };

    const handleRejectPendingChange = async (change: PendingFieldChange) => {
        if (!axios || !props.loiFormId) return;
        await rejectPendingChange(axios, props.loiFormId, change.id);
        props.onPendingChangeResolved?.(change, false);
    };

    const canDiscardPendingChanges = !!props.isAdmin && !props.isInitialQuestionsReadonly;

    const handleDiscardPendingChange = async (change: PendingFieldChange) => {
        await props.onDiscardPendingChange?.(change.fieldKey, change.rowKey);
    };

    const hasDocument = !!uploadedFileName || !!props.documentDetailsId;
    const showDocumentSection = !props.isQuestionsOnly && !props.isTermsOnly && (!props.isAdmin || hasDocument);
    const canUpload = !props.isReadonly && !props.isAdmin && !!props.opportunityId && !!props.loiFormId;

    const handleOpenDocument = () => {
        if (!props.documentDetailsId || !props.opportunityId || !axios) return;
        axios
            .get(
                `/api/marketplace/${props.opportunityId}/MarketplaceOpportunityDocuments/${props.documentDetailsId}`
            )
            .then((response) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                }
            })
            .catch(() =>
                createNotification(
                    'An error occurred while opening the file.',
                    'error'
                )
            );
    };


    const considerationAndEmploymentFields = props.isTermsOnly ? null : (
        <>
            {!props.isInitialSubmission && (
                <div className="loi-preliminary-questions-row">
                    <div className="loi-preliminary-questions-field">
                        <span className="loi-preliminary-questions-label">Is there any consideration paid intended to go to practice employees?</span>
                        <div className="loi-preliminary-questions-value">
                            <TermsFormSelect
                                value={termForm.employeeConsiderationPayer != null ? String(termForm.employeeConsiderationPayer) : ''}
                                options={CONSIDERATION_PAYER_OPTIONS}
                                className="loi-lease-assumption-select"
                                disabled={props.isReadonly}
                                onChange={(value) => props.onChange({ ...termForm, employeeConsiderationPayer: value !== null ? Number(value) as EmployeeConsiderationPayer : null })}
                                aria-label="Is there any consideration paid intended to go to practice employees?"
                                invalid={props.errors?.has('employeeConsiderationPayer')}
                            />
                        </div>
                    </div>
                </div>
            )}
            {props.hasOwnerEmploymentAgreement === true && (
                <div className="loi-preliminary-questions-field loi-preliminary-questions-field--standalone">
                    <span className="loi-preliminary-questions-label">Owner(s) Employment Post-Close</span>
                    <OwnerEmploymentAgreementDurationTable
                        columns={props.ownerEmploymentColumns ?? []}
                        rowDisplayData={props.ownerEmploymentRowDisplayData}
                        tableRows={props.ownerEmploymentTableRows ?? []}
                        rows={termForm.sellingOwnerEmploymentAgreementRows ?? []}
                        isReadonly={!!props.isInitialQuestionsReadonly}
                        hasError={props.errors?.has('sellingOwnerEmploymentAgreementRows') ?? false}
                        treatMissingAsNo={!props.isInitialSubmission}
                        isAdmin={props.isAdmin}
                        onChange={(rows: SellingOwnerEmploymentAgreementRow[]) =>
                            props.onChange({ ...termForm, sellingOwnerEmploymentAgreementRows: rows })
                        }
                        employedPostClosePendingByRow={ownerEmployedPostClosePendingChangesByRow}
                        durationPendingByRow={ownerEmploymentDurationPendingChangesByRow}
                        canApprovePendingChanges={!!props.canApprovePendingChanges}
                        onApprovePendingChange={handleApprovePendingChange}
                        onRejectPendingChange={handleRejectPendingChange}
                        canDiscardPendingChanges={canDiscardPendingChanges}
                        onDiscardPendingChange={handleDiscardPendingChange}
                    />
                </div>
            )}
        </>
    );

    const practicePropertyLeaseFields = props.isTermsOnly ? null : (
        <>
            {props.sellerLocations.length === 1 && (() => {
                const leaseAssumptionPendingChange = findPendingFieldChange(
                    pendingFieldChanges,
                    'leaseAssumption',
                    props.sellerLocations[0].locationId
                );
                const storedLeaseAssumption = termForm.locations?.[0]?.leaseAssumption ?? '';
                const displayedLeaseAssumption = leaseAssumptionPendingChange
                    ? leaseAssumptionPendingChange.proposedValue
                    : storedLeaseAssumption;
                const leaseAssumptionSelect = (
                    <TermsFormSelect
                        value={displayedLeaseAssumption}
                        options={LEASE_ASSUMPTION_OPTIONS}
                        className="loi-lease-assumption-select"
                        disabled={!!props.isInitialQuestionsReadonly || (!!leaseAssumptionPendingChange && !props.isAdmin)}
                        onChange={(value) => {
                            const leaseAssumption = value as TermFormLocation['leaseAssumption'];
                            const location: TermFormLocation = {
                                locationId: props.sellerLocations[0].locationId,
                                locationName: props.sellerLocations[0].locationName,
                                leaseAssumption,
                                leaseType: leaseAssumption === 'new' ? DEFAULT_LEASE_TYPE : null,
                            };
                            props.onChange({ ...termForm, locations: [location] });
                        }}
                        aria-label="Are you expecting to assume an existing lease or assume a new one?"
                        invalid={props.errors?.has('locations')}
                    />
                );
                return (
                    <div className="loi-preliminary-questions-field loi-preliminary-questions-field--standalone">
                        <span className="loi-preliminary-questions-label">Practice Property Lease(s)</span>
                        <div className="loi-preliminary-questions-value">
                            {leaseAssumptionPendingChange ? (
                                <PendingChangeControl
                                    canApprove={!!props.canApprovePendingChanges}
                                    onApprove={() => handleApprovePendingChange(leaseAssumptionPendingChange)}
                                    onReject={() => handleRejectPendingChange(leaseAssumptionPendingChange)}
                                    onDiscard={canDiscardPendingChanges ? () => handleDiscardPendingChange(leaseAssumptionPendingChange) : undefined}>
                                    {leaseAssumptionSelect}
                                </PendingChangeControl>
                            ) : leaseAssumptionSelect}
                        </div>
                    </div>
                );
            })()}
            {props.sellerLocations.length > 1 && (
                <div className="loi-preliminary-questions-field loi-preliminary-questions-field--standalone">
                    <span className="loi-preliminary-questions-label">Practice Property Lease(s)</span>
                    <table className="loi-lease-assumption-table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Assume Existing Lease</th>
                                <th>Execute New Lease</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.sellerLocations.map((loc) => {
                                const selectedValue =
                                    termForm.locations?.find((l) => l.locationId === loc.locationId)
                                        ?.leaseAssumption ?? null;
                                const leaseAssumptionPendingChange = findPendingFieldChange(
                                    pendingFieldChanges,
                                    'leaseAssumption',
                                    loc.locationId
                                );
                                const displayedValue = leaseAssumptionPendingChange
                                    ? (leaseAssumptionPendingChange.proposedValue as TermFormLocation['leaseAssumption'])
                                    : selectedValue;
                                const handleLocationChange = (
                                    leaseAssumption: TermFormLocation['leaseAssumption'],
                                ) => {
                                    const existing = termForm.locations ?? [];
                                    const leaseFieldsClear: Partial<TermFormLocation> = leaseAssumption !== 'new'
                                        ? { termOfLease: null, leaseType: null, leaseRate: null, leaseRateEscalation: null, leaseRateUponExtension: null }
                                        : { leaseType: DEFAULT_LEASE_TYPE };
                                    const updated = existing.some((l) => l.locationId === loc.locationId)
                                        ? existing.map((l) =>
                                            l.locationId === loc.locationId ? { ...l, leaseAssumption, ...leaseFieldsClear } : l,
                                        )
                                        : [
                                            ...existing,
                                            {
                                                locationId: loc.locationId,
                                                locationName: loc.locationName,
                                                leaseAssumption,
                                                ...leaseFieldsClear,
                                            },
                                        ];
                                    props.onChange({ ...termForm, locations: updated });
                                };
                                const renderLeaseRadio = (value: 'existing' | 'new') => (
                                    <div className="custom-control-radio">
                                        <input
                                            type="radio"
                                            id={`lease-assumption-${loc.locationId}-${value}`}
                                            name={`lease-assumption-${loc.locationId}`}
                                            value={value}
                                            checked={displayedValue === value}
                                            disabled={!!props.isInitialQuestionsReadonly || (!!leaseAssumptionPendingChange && !props.isAdmin)}
                                            onChange={() => handleLocationChange(value)}
                                            className="form-check-input"
                                        />
                                        <label
                                            className="label-text"
                                            htmlFor={`lease-assumption-${loc.locationId}-${value}`}
                                        />
                                        <label
                                            htmlFor={`lease-assumption-${loc.locationId}-${value}`}
                                            className={`bullet${displayedValue === null && props.errors?.has('locations') ? ' bullet--invalid' : ''}`}>
                                            <span className="bullet-selected"></span>
                                        </label>
                                    </div>
                                );
                                return (
                                    <tr key={loc.locationId}>
                                        <td className="loi-lease-assumption-table__location-name">
                                            {loc.dbaName}
                                        </td>
                                        <td className="loi-lease-assumption-table__radio-cell">
                                            {leaseAssumptionPendingChange && displayedValue === 'existing' ? (
                                                <PendingChangeControl
                                                    canApprove={!!props.canApprovePendingChanges}
                                                    onApprove={() => handleApprovePendingChange(leaseAssumptionPendingChange)}
                                                    onReject={() => handleRejectPendingChange(leaseAssumptionPendingChange)}
                                                    onDiscard={canDiscardPendingChanges ? () => handleDiscardPendingChange(leaseAssumptionPendingChange) : undefined}>
                                                    {renderLeaseRadio('existing')}
                                                </PendingChangeControl>
                                            ) : renderLeaseRadio('existing')}
                                        </td>
                                        <td className="loi-lease-assumption-table__radio-cell">
                                            {leaseAssumptionPendingChange && displayedValue === 'new' ? (
                                                <PendingChangeControl
                                                    canApprove={!!props.canApprovePendingChanges}
                                                    onApprove={() => handleApprovePendingChange(leaseAssumptionPendingChange)}
                                                    onReject={() => handleRejectPendingChange(leaseAssumptionPendingChange)}
                                                    onDiscard={canDiscardPendingChanges ? () => handleDiscardPendingChange(leaseAssumptionPendingChange) : undefined}>
                                                    {renderLeaseRadio('new')}
                                                </PendingChangeControl>
                                            ) : renderLeaseRadio('new')}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    return (
        <div className="loi-and-terms-form">
            {!props.isInitialSubmission && !props.isQuestionsOnly && (
                <div className="loi-and-terms-form-content">
                    <TermsFormDisplay
                        termForm={termForm}
                        legalIssues={props.formData.legalIssues ?? []}
                        additionalCovenants={props.formData.additionalCovenants ?? []}
                        readOnly={props.isReadonly}
                        onChange={(updated) => props.onChange(updated)}
                        errors={props.errors}
                        isFsb={props.isFsb}
                        isSfas={props.isSfas}
                        isBuyerReview={props.isBuyerReview}
                        isBuyerReviewConfirmed={props.isBuyerReviewConfirmed}
                        fieldComments={props.formData?.termForm?.fieldComments}
                        onFieldCommentChange={props.onFieldCommentChange}
                        onAdminCommentChange={props.onAdminCommentChange}
                        isAdmin={props.isAdmin}
                        canEditFairValues={props.canEditFairValues}
                        isBuyer={props.isBuyer}
                        considerationAndEmploymentSection={
                            considerationAndEmploymentFields && (
                                <div className="loi-preliminary-questions loi-preliminary-questions--above-summary">{considerationAndEmploymentFields}</div>
                            )
                        }
                        practicePropertyLeaseSection={
                            practicePropertyLeaseFields && (
                                <div className="loi-preliminary-questions loi-preliminary-questions--above-summary">{practicePropertyLeaseFields}</div>
                            )
                        }
                    />
                </div>
            )}
            {showDocumentSection && (
                <div className={`loi-document-upload${props.isReadonly ? ' loi-document-upload--readonly' : ''}`}>
                    <div className="loi-document-upload-row">
                        <div className="loi-document-upload-label">{props.isReadonly ? 'LOI File' : 'LOI File Upload'}</div>
                        {canUpload && (
                            <>
                                <ButtonLoader
                                    buttonText="Upload File"
                                    loaderButtonText=""
                                    isLoading={isUploading}
                                    disabled={isUploading}
                                    onClick={handleUploadClick}
                                    className="btn btn-primary"
                                />
                                <input
                                    hidden
                                    ref={fileInputRef}
                                    type="file"
                                    name="loi-document"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                />
                            </>
                        )}
                        {uploadedFileName && (
                            <div
                                className="loi-document-upload-filename loi-document-upload-filename--clickable"
                                onClick={handleOpenDocument}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOpenDocument();
                                    }
                                }}
                                role="button"
                                tabIndex={0}>
                                {uploadedFileName}
                            </div>
                        )}
                    </div>
                    {props.loiFileError && (
                        <div className="loi-document-upload-error text-danger">LOI file is required</div>
                    )}
                </div>
            )}
            {props.isInitialSubmission && !props.isAdmin && !props.isQuestionsOnly && (
                <div className="loi-legal-info-confirm">
                    <SellerLegalInfoSection />
                    <div className="loi-legal-info-confirm__checkbox-row">
                        <input
                            type="checkbox"
                            id="loi-legal-info-confirm"
                            checked={!!termForm.isLegalInfoConfirmed}
                            disabled={props.isReadonly}
                            className={props.errors?.has('isLegalInfoConfirmed') ? 'loi-legal-info-confirm__checkbox--invalid' : undefined}
                            onChange={(e) => props.onChange({ ...termForm, isLegalInfoConfirmed: e.target.checked })}
                        />
                        <label htmlFor="loi-legal-info-confirm" className="loi-legal-info-confirm__label">
                            This seller information matches that contained in the uploaded LOI
                        </label>
                    </div>
                </div>
            )}
            {!props.isTermsOnly && <div className="loi-preliminary-questions">
                {(props.isInitialSubmission || props.isQuestionsOnly) && considerationAndEmploymentFields}
                {(props.isInitialSubmission || props.isQuestionsOnly) && practicePropertyLeaseFields}
                {props.hasVendorContracts === true && (
                    <div className="loi-preliminary-questions-field loi-preliminary-questions-field--standalone">
                        <span className="loi-preliminary-questions-label">Seller Contractual Obligations</span>
                        <div className="loi-preliminary-questions-value">
                            <VendorContractsTable
                                columns={props.vendorContractColumns ?? []}
                                rowDisplayData={props.vendorContractRowDisplayData}
                                tableRows={props.vendorContractTableRows ?? []}
                                selectedRowIds={(termForm.vendorContractRows ?? []).filter((r) => r.isAssumedByBuyer !== false).map((r) => r.tableRowId)}
                                payoffAtCloseRowIds={(termForm.vendorContractRows ?? []).filter((r) => r.isAssumedByBuyer === false).map((r) => r.tableRowId)}
                                treatMissingAsPayoff={!props.isInitialSubmission}
                                hasError={props.errors?.has('vendorContractRows') ?? false}
                                isReadonly={!!props.isInitialQuestionsReadonly}
                                onAssumptionToggle={(tableRowId, isAssumed) => {
                                    const existing = termForm.vendorContractRows ?? [];
                                    const found = existing.find((r) => r.tableRowId === tableRowId);
                                    const newRows: VendorContractRow[] = found
                                        ? existing.map((r) => r.tableRowId === tableRowId ? { ...r, isAssumedByBuyer: isAssumed } : r)
                                        : [...existing, { id: crypto.randomUUID(), tableRowId, isAssumedByBuyer: isAssumed }];
                                    props.onChange({ ...termForm, vendorContractRows: newRows });
                                    if (props.deferAssumptionSave || !axios || !props.loiFormId) {
                                        return;
                                    }
                                    setVendorContractAssumption(axios, props.loiFormId, { tableRowId, isAssumed }).catch(() => createNotification('Failed to save vendor contract assumption.', 'error'));
                                }}
                                projectId={props.vendorContractProjectId}
                                tableId={props.vendorContractTableId}
                                fileColumnName={props.vendorContractFileColumnName}
                                fileColumnId={props.vendorContractFileColumnId}
                                isAdmin={props.isAdmin}
                                pendingChangesByRow={vendorPendingChangesByRow}
                                canApprovePendingChanges={!!props.canApprovePendingChanges}
                                onApprovePendingChange={handleApprovePendingChange}
                                onRejectPendingChange={handleRejectPendingChange}
                                canDiscardPendingChanges={canDiscardPendingChanges}
                                onDiscardPendingChange={handleDiscardPendingChange}
                            />
                        </div>
                    </div>
                )}
            </div>}
        </div>
    );
};
